import {
  getDaysRemainingInCurrentMonth,
  getShortMonthName,
} from '@nextforce/api'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { t } from 'app/i18l'
import { useServer } from 'app/server/context'
import { BaseHomeItem, HomeItem, SummaryItem } from 'home/types'
import {
  makeErrorHomeItem,
  makeSuccessHomeItem,
} from 'home/update_app/shared/utils'
import { RetryQuery } from 'shared/queries/utils'
import { useUser } from 'users/queries/detail'
import { MonthlyEarnings } from '../types'
import { convertMonthlySummary } from './shared/convert'
import keys from './shared/keys'
import * as Response from './shared/response'

export function useMonthlySummary<T = MonthlyEarnings>(
  enabled = true,
  select?: (data: MonthlyEarnings) => T,
  retryQuery?: RetryQuery
) {
  const { server } = useServer()
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: keys.all,
    queryFn: fetchMonthlySummary,
    enabled: enabled,
    select,
    retry: retryQuery ?? false,
  })

  if (!enabled) {
    queryClient.removeQueries({ queryKey: keys.all })
  }

  return query

  async function fetchMonthlySummary() {
    const response = await server.get<Response.MonthlyEarnings>(
      `/v2/users/me/monthly_earnings`
    )
    return convertMonthlySummary(response)
  }
}

export function useMonthlySummaryHomeItem(): HomeItem | undefined {
  const { data: user } = useUser()
  const enabled = user?.permissions?.canSeeMonthlySummary
  const {
    data: monthlyEarningsData,
    isError: isMonthlyEarningsError,
    error: monthlyEarningsError,
  } = useMonthlySummary(enabled)
  const baseItem = {
    title: t('monthlyProgress'),
    route: { name: 'open_shifts' },
    color: 'white',
    backgroundColor: '#4E5665',
  } as BaseHomeItem

  if (!monthlyEarningsData?.monthlyEarningsTargetEnabled || !enabled) {
    return
  }

  if (isMonthlyEarningsError) {
    return makeErrorHomeItem(baseItem, monthlyEarningsError)
  }
  const summaryItems: SummaryItem[] =
    monthlyEarningsData?.monthlySummaries?.map(summary => {
      const earningsRate = t('earningsRate', {
        rate: Math.round(summary.rate),
      })
      const priorMonthMessage =
        summary.delta > 0
          ? t('priorMonthTargetMetMessage')
          : t('priorMonthTargetNotMetMessage')
      const currentMonthMessage =
        summary.delta > 0
          ? t('currentMonthTargetMetMessage')
          : t('currentMonthTargetNotMetMessage')

      return {
        title: summary.isCurrentMonth
          ? `${t('daysToGo', {
              days: getDaysRemainingInCurrentMonth(),
            })} - ${currentMonthMessage}`
          : `${getShortMonthName(
              summary.month
            )}: ${priorMonthMessage} - ${earningsRate}`,

        params: {
          actual: {
            ...summary.actual,
            title: summary.isCurrentMonth ? t('currentEstimate') : t('actual'),
          },
          target: { ...summary.target, title: t('monthlyTarget') },
          month: summary.month,
          isCurrentMonth: summary.isCurrentMonth,
          delta: summary.delta,
        },
        contentComponentKey: 'MONTHLY_SUMMARY_ITEM_CONTENT',
      }
    }) ?? []

  if (summaryItems.length > 0) {
    summaryItems.push({
      title: t('monthlyEarningsDescriptionTitle'),
      description: t('monthlyProgressExplanationMessage'),
      contentComponentKey: 'MONTHLY_SUMMARY_DESCRIPTION_ITEM_CONTENT',
    })
  }

  return makeSuccessHomeItem(baseItem, summaryItems)
}
